import api from 'api';

const SCHEMA_VERSION = 1;
const ACTIVE_CHECKPOINT_MS = 60 * 1000;

const createId = () => {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}_${Math.random().toString(36).slice(2)}_${Math.random()
    .toString(36)
    .slice(2)}`;
};

const toSeconds = milliseconds => Number((Math.max(0, milliseconds) / 1000).toFixed(1));

const classifyError = error => {
  const status = Number(error && error.status);

  if (!window.navigator.onLine || (error && error.name === 'TypeError')) {
    return 'network';
  }

  if ([401, 403].includes(status)) {
    return 'authorization';
  }

  if (status >= 400 && status < 500) {
    return 'validation';
  }

  if (status >= 500) {
    return 'server';
  }

  return 'unknown';
};

const startSession = (surface, schemaVersion = SCHEMA_VERSION) => {
  const sessionId = createId();
  const startedAt = Date.now();
  let activeStartedAt =
    document.visibilityState === 'visible' && document.hasFocus() ? startedAt : null;
  let activeDurationMs = 0;
  let lastReportedActiveMs = 0;
  let checkpointSequence = 0;
  let stopped = false;

  const getActiveDurationMs = (now = Date.now()) =>
    activeDurationMs + (activeStartedAt === null ? 0 : now - activeStartedAt);

  const track = (eventType, eventProperties = {}) => {
    try {
      return api.moderationTelemetry.track({
        event_type: eventType,
        event_time_ms: Date.now(),
        insert_id: createId(),
        event_properties: {
          schema_version: schemaVersion,
          session_id: sessionId,
          surface,
          ...eventProperties,
        },
      });
    } catch (error) {
      return Promise.resolve();
    }
  };

  const setForeground = isForeground => {
    const now = Date.now();

    if (activeStartedAt !== null) {
      activeDurationMs += now - activeStartedAt;
      activeStartedAt = null;
    }

    if (isForeground && !stopped) {
      activeStartedAt = now;
    }
  };

  const checkpoint = () => {
    if (stopped) {
      return;
    }

    const now = Date.now();
    const totalActiveMs = getActiveDurationMs(now);
    const intervalActiveMs = totalActiveMs - lastReportedActiveMs;

    if (intervalActiveMs < 1000) {
      return;
    }

    checkpointSequence += 1;
    lastReportedActiveMs = totalActiveMs;
    track('MODERATION_SESSION_ACTIVE', {
      active_duration_seconds: toSeconds(totalActiveMs),
      active_interval_seconds: toSeconds(intervalActiveMs),
      session_duration_seconds: toSeconds(now - startedAt),
      checkpoint_sequence: checkpointSequence,
    });
  };

  const onVisibilityChange = () =>
    setForeground(document.visibilityState === 'visible' && document.hasFocus());
  const onFocus = () => setForeground(document.visibilityState === 'visible');
  const onBlur = () => setForeground(false);

  const stop = endReason => {
    if (stopped) {
      return;
    }

    setForeground(false);
    stopped = true;
    const now = Date.now();
    const intervalActiveMs = activeDurationMs - lastReportedActiveMs;

    track('MODERATION_SESSION_ENDED', {
      active_duration_seconds: toSeconds(activeDurationMs),
      active_interval_seconds: toSeconds(intervalActiveMs),
      session_duration_seconds: toSeconds(now - startedAt),
      end_reason: endReason,
    });

    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('blur', onBlur);
    window.removeEventListener('pagehide', onPageHide);
    window.clearInterval(checkpointTimer);
  };

  const onPageHide = () => stop('pagehide');
  const checkpointTimer = window.setInterval(checkpoint, ACTIVE_CHECKPOINT_MS);

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', onFocus);
  window.addEventListener('blur', onBlur);
  window.addEventListener('pagehide', onPageHide);

  track('MODERATION_SESSION_STARTED');

  return {
    sessionId,
    track,
    stop,
  };
};

const moderationTelemetry = {
  classifyError,
  createId,
  startSession,
};

export default moderationTelemetry;
